import { supabase, handleSupabaseError, withRetry, getNetworkStatus } from '../lib/supabase';

// Mock data for offline fallback
const mockTeamMembers = [
  {
    id: 'mock-1',
    name: 'Sarah Johnson',
    title: 'Lead Cleaning Specialist',
    bio: 'With over 8 years of experience in commercial and residential cleaning.',
    years_experience: 8,
    specialties: ['Deep Cleaning', 'Eco-Friendly Solutions'],
    photo_url: null,
    is_active: true,
    sort_order: 1,
    created_at: '2024-01-01T00:00:00.000Z'
  },
  {
    id: 'mock-2',
    name: 'Mike Chen',
    title: 'Operations Manager',
    bio: 'Ensures quality service delivery and customer satisfaction.',
    years_experience: 6,
    specialties: ['Quality Control', 'Customer Service'],
    photo_url: null,
    is_active: true,
    sort_order: 2,
    created_at: '2024-01-01T00:00:00.000Z'
  },
  {
    id: 'mock-3',
    name: 'Emma Rodriguez',
    title: 'Senior Cleaner',
    bio: 'Specialized in post-construction cleanup and detailed sanitization.',
    years_experience: 5,
    specialties: ['Post-Construction', 'Sanitization'],
    photo_url: null,
    is_active: true,
    sort_order: 3,
    created_at: '2024-01-01T00:00:00.000Z'
  }
];

export class TeamService {
  // Get all active team members with enhanced error handling
  static async getTeamMembers() {
    try {
      // Check network status first
      if (!getNetworkStatus()) {
        console.warn('Network offline - returning mock team members');
        return mockTeamMembers;
      }

      const operation = async () => {
        const { data, error } = await supabase?.from('team_members')?.select('*')?.eq('is_active', true)?.order('sort_order', { ascending: true });
        
        if (error) {
          throw error;
        }
        
        return data || [];
      };

      const result = await withRetry(operation, 2, 1500);
      
      // Return mock data if no results and we're having connectivity issues
      if (!result || result?.length === 0) {
        console.warn('No team members found in database - using fallback data');
        return mockTeamMembers;
      }
      
      return result;
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.getTeamMembers');
      
      // Return mock data for network errors
      if (error?.message?.includes('Failed to fetch') || !getNetworkStatus()) {
        console.warn('Falling back to mock team members due to connectivity issues');
        return mockTeamMembers;
      }
      
      // For other errors, return empty array
      console.error('Error fetching team members:', errorInfo);
      return [];
    }
  }

  // Create a new team member
  static async createTeamMember(memberData) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot create team member while offline');
      }

      const operation = async () => {
        const { data, error } = await supabase?.from('team_members')?.insert([memberData])?.select()?.single();
        
        if (error) {
          throw error;
        }
        
        return data;
      };

      return await withRetry(operation, 2, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.createTeamMember');
      throw new Error(`Failed to create team member: ${errorInfo.message}`);
    }
  }

  // Update team member
  static async updateTeamMember(id, memberData) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot update team member while offline');
      }

      const operation = async () => {
        const { data, error } = await supabase?.from('team_members')?.update(memberData)?.eq('id', id)?.select()?.single();
        
        if (error) {
          throw error;
        }
        
        return data;
      };

      return await withRetry(operation, 2, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.updateTeamMember');
      throw new Error(`Failed to update team member: ${errorInfo.message}`);
    }
  }

  // Delete team member
  static async deleteTeamMember(id) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot delete team member while offline');
      }

      const operation = async () => {
        const { error } = await supabase?.from('team_members')?.delete()?.eq('id', id);
        
        if (error) {
          throw error;
        }
        
        return true;
      };

      return await withRetry(operation, 2, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.deleteTeamMember');
      throw new Error(`Failed to delete team member: ${errorInfo.message}`);
    }
  }

  // Upload team member photo
  static async uploadPhoto(file, memberId) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot upload photo while offline');
      }

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${memberId}/photo.${fileExt}`;

      const operation = async () => {
        const { data, error } = await supabase?.storage?.from('team-photos')?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

        if (error) {
          throw error;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase?.storage?.from('team-photos')?.getPublicUrl(fileName);
        return publicUrl;
      };

      return await withRetry(operation, 2, 1500);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.uploadPhoto');
      throw new Error(`Failed to upload photo: ${errorInfo.message}`);
    }
  }

  // Delete team member photo
  static async deletePhoto(memberId) {
    try {
      if (!getNetworkStatus()) {
        throw new Error('Cannot delete photo while offline');
      }

      const extensions = ['jpg', 'jpeg', 'png', 'webp'];
      
      const operation = async () => {
        for (const ext of extensions) {
          const fileName = `${memberId}/photo.${ext}`;
          await supabase?.storage?.from('team-photos')?.remove([fileName]);
        }
        return true;
      };

      return await withRetry(operation, 1, 1000);
    } catch (error) {
      const errorInfo = handleSupabaseError(error, 'TeamService.deletePhoto');
      console.warn('Error deleting photo (non-critical):', errorInfo);
      return true; // Non-critical operation
    }
  }

  // Connection status check
  static async checkConnection() {
    try {
      if (!getNetworkStatus()) {
        return false;
      }

      const { error } = await supabase?.from('team_members')?.select('count')?.limit(1);
      return !error;
    } catch (error) {
      return false;
    }
  }
}